import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  FileText,
  X,
  Loader2,
  Filter,
  Calendar,
  User,
  FolderOpen,
  SortDesc,
  Search,
  Sparkles
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSearch, useSearchSuggestions, useCategories } from "@/hooks/useKBData";
import { useDebounce } from "@/hooks/useDebounce";
import { SearchFilters, applyClientSideFilters } from "@/lib/kbApi";
import { formatDistanceToNow } from "date-fns";

interface EnhancedSearchCommandProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EnhancedSearchCommand({ isOpen, onClose }: EnhancedSearchCommandProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({
    contentType: 'all',
    sortBy: 'relevance'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('kb-recent-searches') || '[]');
    } catch {
      return [];
    }
  });

  // Debounce search query to prevent API spam
  const debouncedQuery = useDebounce(query, 300);

  // Fetch search results and suggestions
  const { data: searchData, isLoading: searchLoading } = useSearch(debouncedQuery);
  const { data: suggestionsData } = useSearchSuggestions(debouncedQuery);
  const { data: categoriesData } = useCategories();

  const rawResults = searchData?.articles || [];
  const suggestions = suggestionsData?.suggestions || [];
  const categories = categoriesData?.categories || [];

  // Apply client-side filtering until backend supports it
  const searchResults = debouncedQuery && rawResults.length > 0
    ? applyClientSideFilters(rawResults, filters, categories)
    : rawResults;

  const saveRecentSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) return;

    const updated = [
      searchTerm,
      ...recentSearches.filter(s => s !== searchTerm)
    ].slice(0, 5);

    setRecentSearches(updated);
    localStorage.setItem('kb-recent-searches', JSON.stringify(updated));
  };

  const handleArticleSelect = (slug: string) => {
    saveRecentSearch(query);
    navigate(`/app/kb/${slug}`);
    onClose();
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ contentType: 'all', sortBy: 'relevance' });
  };

  const activeFiltersCount = Object.values(filters).filter(v =>
    v && v !== 'all' && v !== 'relevance'
  ).length;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl mx-4 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
        <Command className="rounded-2xl border-0" shouldFilter={false}>
          {/* Search Input with Filters */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            <div className="flex items-center flex-1 gap-3">
              <Search className="h-5 w-5 text-muted-foreground shrink-0" />
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Search articles, content, or ask a question..."
                className="flex-1 bg-transparent text-lg border-0 outline-none placeholder:text-muted-foreground [&_svg]:hidden"
              />
              {searchLoading && debouncedQuery && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
              )}
            </div>

            {/* Filter Toggle */}
            <Popover open={showFilters} onOpenChange={setShowFilters}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative shrink-0"
                >
                  <Filter className="h-4 w-4" />
                  {activeFiltersCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 text-xs flex items-center justify-center"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Search Filters</h3>
                    {activeFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        Clear
                      </Button>
                    )}
                  </div>

                  {/* Category Filter */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <FolderOpen className="h-3 w-3" />
                      Category
                    </label>
                    <Select
                      value={filters.category || ""}
                      onValueChange={(value) => handleFilterChange('category', value || undefined)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any category</SelectItem>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.name}>
                            {category.icon} {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Range Filter */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Date Range
                    </label>
                    <Select
                      value={filters.dateRange || ""}
                      onValueChange={(value) => handleFilterChange('dateRange', value || undefined)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any time</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="week">This week</SelectItem>
                        <SelectItem value="month">This month</SelectItem>
                        <SelectItem value="year">This year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Content Type Filter */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">
                      Search In
                    </label>
                    <Select
                      value={filters.contentType || "all"}
                      onValueChange={(value) => handleFilterChange('contentType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Title & Content</SelectItem>
                        <SelectItem value="title">Title only</SelectItem>
                        <SelectItem value="content">Content only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort Order */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <SortDesc className="h-3 w-3" />
                      Sort By
                    </label>
                    <Select
                      value={filters.sortBy || "relevance"}
                      onValueChange={(value) => handleFilterChange('sortBy', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="relevance">Relevance</SelectItem>
                        <SelectItem value="updated">Recently updated</SelectItem>
                        <SelectItem value="created">Recently created</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <CommandList className="max-h-[60vh]">
            {/* No Query State - Recent Searches & Suggestions */}
            {debouncedQuery.length === 0 && (
              <>
                {recentSearches.length > 0 && (
                  <CommandGroup heading="Recent Searches" className="p-4 pb-2">
                    {recentSearches.map((search, index) => (
                      <CommandItem
                        key={`${search}-${index}`}
                        onSelect={() => setQuery(search)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer data-[selected=true]:bg-muted"
                      >
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{search}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                <CommandGroup heading="Browse by Category" className="px-4 pb-4">
                  <div className="flex flex-wrap gap-2 pt-2">
                    {categories.map((category) => (
                      <Badge
                        key={category.id}
                        variant="outline"
                        className="cursor-pointer hover:bg-muted transition-colors px-3 py-1.5 rounded-lg text-xs"
                        onClick={() => handleFilterChange('category', category.name)}
                      >
                        {category.icon} {category.name}
                      </Badge>
                    ))}
                  </div>
                </CommandGroup>
              </>
            )}

            {/* Search Suggestions */}
            {debouncedQuery.length > 0 && suggestions.length > 0 && searchResults.length === 0 && !searchLoading && (
              <CommandGroup heading="Suggestions" className="p-2">
                {suggestions.map((suggestion, index) => (
                  <CommandItem
                    key={`${suggestion}-${index}`}
                    onSelect={() => setQuery(suggestion)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer data-[selected=true]:bg-muted"
                  >
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="text-sm">{suggestion}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Search Results */}
            {debouncedQuery.length > 0 && searchResults.length > 0 && (
              <CommandGroup
                heading={`Results (${searchResults.length})${activeFiltersCount > 0 ? ' - Filtered' : ''}`}
                className="p-2"
              >
                {searchResults.map((article) => {
                  const category = categories.find((c) => c.id === article.category_id);
                  return (
                    <CommandItem
                      key={article.id}
                      onSelect={() => handleArticleSelect(article.slug)}
                      className="flex items-start gap-3 px-3 py-3 rounded-xl cursor-pointer data-[selected=true]:bg-primary/10"
                    >
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-sm truncate">{article.title}</span>
                          <ArrowRight className="h-3 w-3 text-primary shrink-0 data-[selected=false]:opacity-0" />
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {article.excerpt || "No preview available"}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {category?.name || "Uncategorized"}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            Updated {formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* No Results */}
            {debouncedQuery.length > 0 && !searchLoading && searchResults.length === 0 && suggestions.length === 0 && (
              <CommandEmpty className="p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
                  <Search className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold mb-1">No results found</h3>
                <p className="text-sm text-muted-foreground">
                  Try different keywords or clear your filters
                </p>
                {activeFiltersCount > 0 && (
                  <Button variant="outline" size="sm" onClick={clearFilters} className="mt-3">
                    <Filter className="h-3 w-3 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </CommandEmpty>
            )}

            {/* Loading State */}
            {debouncedQuery.length > 0 && searchLoading && (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Searching...</p>
              </div>
            )}
          </CommandList>
        </Command>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">↓</kbd>
              <span>Navigate</span>
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">↵</kbd>
              <span>Open</span>
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">⌘K</kbd>
              <span>Search</span>
            </span>
          </div>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">esc</kbd>
            <span>Close</span>
          </span>
        </div>
      </div>
    </div>
  );
}